import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useTranslation} from '@aboutyou/i18n';
import {useHistory} from '@aboutyou/router';
import {
  COLORS,
  desktopAndUp,
  Dropdown,
  FONT_SIZES,
  FONT_WEIGHTS,
  RadioButton,
  RadioGroup,
  tabletAndDown,
  TextInput,
} from '@aboutyou/ui-components';
import React, {useEffect, useState} from 'react';
import {useFieldArray, useForm} from 'react-hook-form';
import styled, {css} from 'styled-components';
import {updateUrlParams} from '../../shared-deprecated/updateUrlParams';

type FormData = {fieldArray: {itemNumbers: string}[]};

const labelTransitionTime = '100ms';
export const DynamicItemForm = ({
  onSubmit,
}: {
  onSubmit: (ids: string[]) => void;
}) => {
  const {t} = useTranslation();
  const history = useHistory();
  const [formSavedData, setFormSavedData] = useHistoryPageState<
    undefined | FormData
  >(undefined, {
    uniqueKey: 'DynamicDeclarationOfLossForm',
  });
  const {
    control,
    register,
    getValues,
    formState: {errors},
    watch,
    trigger,
  } = useForm<FormData>({
    mode: 'onChange',
  });
  const {fields, append, remove, replace} = useFieldArray({
    control,
    name: 'fieldArray' as never,
  });

  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const [amountOfItems, setAmountOfItems] = useState<number>(0);

  const onItemCountChange = (value: number) => {
    setAmountOfItems(value);
    history.replace({
      state: history.location.state,
      search: updateUrlParams({
        search: history.location.search,
        update: new Map([['itemCount', [value.toString()]]]),
      }),
    });

    // cleanup field from before creating new
    remove();
    for (let index = 0; index < value; index++) {
      append({
        itemNumbers: '',
      });
    }
    setOpenDropdown(false);
  };

  useEffect(() => {
    const allParams = new URLSearchParams(history.location.search ?? '');
    const itemCount = allParams.get('itemCount');
    if (itemCount) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      onItemCountChange(parseInt(itemCount, 10));
    }
  }, []);

  useEffect(() => {
    const subscription = watch((value, _) => {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setFormSavedData(value as FormData);
      trigger().then(() => {
        submitData(formSavedData);
      });
    });
    return () => {
      if (subscription && subscription.unsubscribe) {
        subscription.unsubscribe();
      }
    };
  }, [setFormSavedData, watch]);

  const submitData = (passedData?: FormData) => {
    const returnValue: string[] = [];
    const filledValues = getValues();
    let areAllFieldsFilledOut = true;

    (passedData || filledValues).fieldArray.forEach(item => {
      if (item.itemNumbers) {
        returnValue.push(item.itemNumbers);
      } else {
        areAllFieldsFilledOut = false;
      }
    });
    if (areAllFieldsFilledOut) {
      onSubmit(returnValue);
    }
  };
  // fill out data if it exists from history
  useEffect(() => {
    if (formSavedData) {
      replace(formSavedData.fieldArray);
      trigger().then(() => {
        submitData(formSavedData);
      });
    }
  }, []);

  return (
    <>
      <StyledDropdown
        onClose={() => {
          setOpenDropdown(false);
        }}
        isActive={Boolean(amountOfItems)}
        forceBlackBorderOnHover
        onOpenerClick={() => {
          setOpenDropdown(true);
        }}
        open={openDropdown}
        openerContent={
          <OpenerWrapper>
            {amountOfItems !== 0 && (
              <LabelText offsetLeft={16}>
                {t('How many items do you want to report?')}
              </LabelText>
            )}
            <PlaceholderText
              data-testid={'textInputPlaceholder'}
              offsetLeft={16}
            >
              {amountOfItems || t('How many items do you want to report?')}
            </PlaceholderText>
          </OpenerWrapper>
        }
        bodyContent={
          <ItemNumberRadioGroup>
            {Array.from({length: 10}, (_, k) => {
              const value = k + 1;
              return (
                <StyledRadioButton
                  name="numberOfItems"
                  label={value}
                  value={value}
                  onChange={() => onItemCountChange(value)}
                />
              );
            })}
          </ItemNumberRadioGroup>
        }
        openerType={'caret'}
      />

      <StyledForm
        autoComplete="on"
        data-testid="item-id-dynamic-form"
        onSubmit={() => {}}
        onBlur={() => {
          // blur misses one trigger, check again what is happening
          submitData();
        }}
      >
        {fields.map((field, index) => {
          // here we are declaring where values are getting saved and how
          const inputName = `fieldArray[${index}].itemNumbers` as string;

          return (
            <TextInput
              key={field.id} // important to include key with field's id
              activeStyle={{
                backgroundColor: COLORS.White,
                borderColor: COLORS.Black,
              }}
              autoComplete={inputName}
              data-testid={`declarationOfLossDynamic${inputName}`}
              errorMessage={(errors as any)[inputName]?.message}
              label={t('Item number*')}
              // registration is complaining if string is a path -> works fine but types are messed up
              {...register(inputName as any, {
                required: t('Invalid format'),
              })}
              type="text"
              value={getValues(inputName as any)}
            />
          );
        })}
      </StyledForm>
    </>
  );
};

const PlaceholderText = styled.span<{offsetLeft: number}>`
  position: absolute;
  left: 0px;
  top: 0px;
  transform: ${({offsetLeft}) => `translate(${offsetLeft}px, 15px)`};
  pointer-events: none;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  color: ${COLORS.Black};
  padding-left: 2px;
  padding-right: 2px;
  transition: opacity ${labelTransitionTime};
`;

const LabelText = styled.span<{
  offsetLeft: number;
}>`
  position: absolute;
  left: 0;
  top: -22px;
  transform: ${({offsetLeft}) => `translate(${offsetLeft}px, 15px)`};
  pointer-events: none;
  font-weight: ${FONT_WEIGHTS.mediumBold};
  transition: transform ${labelTransitionTime}, color ${labelTransitionTime},
    font-size ${labelTransitionTime}, opacity ${labelTransitionTime};
  padding-left: 4px;
  padding-right: 4px;
  background-color: ${COLORS.White};
  font-size: ${FONT_SIZES.px10};
  color: ${COLORS.Black};
`;

const StyledDropdown = styled(Dropdown)`
  ${tabletAndDown(css`
    margin: 16px 0;
  `)}
`;

const OpenerWrapper = styled.div`
  padding: 23px;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  position: relative;
  width: 100%;
  color: ${COLORS.Black};
`;

const ItemNumberRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${tabletAndDown(css`
    margin-bottom: 1rem;
  `)}

  ${desktopAndUp(css`
    gap: 24px;
  `)}
`;

const StyledRadioButton = styled(RadioButton)`
  transition: background-color 0.2s ease-in-out;
  &&:not(:last-child) {
    margin-right: 0;
  }

  > span {
    color: ${COLORS.Black};
  }

  &:first {
    padding: 16px 8px 8px 16px;
  }

  padding: 8px 8px 8px 16px;

  ${desktopAndUp(css`
    &:first {
      padding: 16px 8px 12px 16px;
    }

    padding: 12px 8px 12px 16px;
  `)}

  &:hover {
    cursor: pointer;
    background-color: ${COLORS.Grey4};
  }
`;
